import React from 'react'
import { useNavigate } from 'react-router-dom'

import { AlertModal, ConfirmModal, useModalContext } from '@fourthwall/components'
import { useCurrentShop } from '@modules/Dashboard/hooks/useCurrentShop'
import { routing } from '@utils/routing'

import { useAccountSettingsQuery } from '../../Settings/hooks/useAccountSettingsQuery'
import type { Tier } from '../types'
import { useDeleteFreeTierMutation } from './useDeleteFreeTierMutation'
import { useDeleteTierMutation } from './useDeleteTierMutation'
import { usePublishTierMutation } from './usePublishTierMutation'
import { useUnpublishTierMutation } from './useUnpublishTierMutation'

export const useTierActions = () => {
  const navigate = useNavigate()
  const modal = useModalContext()
  const { currentShop } = useCurrentShop()

  const { accountSettingsQuery } = useAccountSettingsQuery()

  const { deleteTierMutation } = useDeleteTierMutation()
  const { deleteFreeTierMutation } = useDeleteFreeTierMutation()
  const { publishTierMutation } = usePublishTierMutation()
  const { unpublishTierMutation } = useUnpublishTierMutation()

  const isLoading =
    accountSettingsQuery.isFetching ||
    publishTierMutation.isLoading ||
    unpublishTierMutation.isLoading ||
    deleteTierMutation.isLoading ||
    deleteFreeTierMutation.isLoading

  const publishTier = (tier: Tier, onSuccess: () => void) => {
    return modal.open(ConfirmModal, {
      title: 'Publish tier',
      text: 'This tier will become visible on your site and available for new members to subscribe.',
      confirmLabel: 'Publish tier',
      confirmAppearance: 'destructive',
      onConfirm: () => {
        publishTierMutation.mutate([tier.id], {
          onSuccess,
        })
      },
    })
  }

  const unpublishTier = (tier: Tier, onSuccess: () => void) => {
    // Exclude Core Of The Heart
    if (tier.mobileWlaEnabled && currentShop?.id !== 'sh_940deabb-e281-4a37-97af-52d4685380fb') {
      return modal.open(AlertModal, {
        title: 'Unpublishing this tier is not possible',
        text: 'A tier available on mobile apps cannot be unpublished.',
        size: 'small',
      })
    }

    if (accountSettingsQuery.data?.upselling?.tierId === tier.id) {
      return modal.open(ConfirmModal, {
        title: 'Unpublish action unavailable',
        text: 'This tier cannot be unpublished because it’s set in the shop upselling promotion. Deactivate the promotion or change its tier to continue.',
        confirmLabel: 'Edit upselling',
        confirmAppearance: 'destructive',
        onConfirm: () => {
          navigate(routing.memberships.settings.upselling)
        },
      })
    }

    return modal.open(ConfirmModal, {
      title: 'Unpublish tier',
      text: (
        <>
          <p style={{ marginBottom: 10 }}>
            Unpublishing will hide this tier from your site. No new members will be able to
            subscribe.
          </p>
          <p>
            Tier will remain active for currently subscribed members. You can republish the tier at
            any time.
          </p>
        </>
      ),
      confirmLabel: 'Unpublish tier',
      confirmAppearance: 'destructive',
      onConfirm: () => {
        unpublishTierMutation.mutate([tier.id], {
          onSuccess,
        })
      },
    })
  }

  const deleteTier = (tier: Tier, onSuccess: () => void) => {
    if (tier.statistics.membersCount > 0) {
      return modal.open(AlertModal, {
        title: 'Deleting this tier is not possible',
        text: 'A tier with active members cannot be deleted.',
        size: 'small',
      })
    }

    if (accountSettingsQuery.data?.upselling?.tierId === tier.id) {
      return modal.open(ConfirmModal, {
        title: 'Delete action unavailable',
        text: 'This tier cannot be deleted because it’s set in the shop upselling promotion. Deactivate the promotion or change its tier to continue.',
        confirmLabel: 'Edit upselling',
        confirmAppearance: 'destructive',
        onConfirm: () => {
          navigate(routing.memberships.settings.upselling)
        },
      })
    }

    return modal.open(ConfirmModal, {
      title: 'Delete tier',
      text: 'Are you sure you want to delete this tier?',
      confirmLabel: 'Delete tier',
      confirmAppearance: 'destructive',
      onConfirm: () => {
        deleteTierMutation.mutate([tier.id], {
          onSuccess,
        })
      },
    })
  }

  const deleteFreeTier = (onSuccess: () => void) => {
    return modal.open(ConfirmModal, {
      title: 'Delete tier',
      text: 'Are you sure you want to delete this tier? Free members will lose access to their perks, but will still have access to your members only area as free members.',
      confirmLabel: 'Delete tier',
      confirmAppearance: 'destructive',
      onConfirm: () => {
        deleteFreeTierMutation.mutate([], {
          onSuccess,
        })
      },
    })
  }

  return {
    deleteTier,
    deleteFreeTier,
    publishTier,
    unpublishTier,
    isLoading,
  }
}
